// Coupon landing page — /redeem/DIRECT10-XXXX
// Deep-link used in email, SMS, printed QR codes so the discount CTA works
// even outside our normal site flows.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, Copy, Check, MessageCircle, AlertTriangle, Home, Loader2 } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  en: {
    eyebrow: 'Personal offer', title: 'You have 10% off direct',
    body: 'Book direct with Hotel La Rosa Negra and save. Message our team on WhatsApp with your code and we\'ll apply the discount instantly.',
    copy: 'Copy code', copied: 'Copied!', whatsapp: 'Redeem on WhatsApp',
    expires: 'Valid until', backHome: 'Visit the hotel',
    expired: 'This code has expired', redeemed: 'This code has already been redeemed', notFound: 'Code not found',
    expiredBody: 'Contact us on WhatsApp anyway — we\'ll do our best.',
    whatsappMsg: (code) => `Hi Hotel La Rosa Negra! I'd like to book direct with my 10% discount code: ${code}`,
  },
  me: {
    eyebrow: 'Lična ponuda', title: 'Vaš popust od 10% za direktnu rezervaciju',
    body: 'Rezervišite direktno kod nas i uštedite. Pošaljite nam poruku na WhatsApp sa svojim kodom, popust primjenjujemo odmah.',
    copy: 'Kopiraj kod', copied: 'Kopirano!', whatsapp: 'Iskoristi na WhatsApp',
    expires: 'Važi do', backHome: 'Posjeti hotel',
    expired: 'Ovaj kod je istekao', redeemed: 'Ovaj kod je već iskorišćen', notFound: 'Kod nije pronađen',
    expiredBody: 'Pošaljite nam poruku na WhatsApp — pokušaćemo pomoći.',
    whatsappMsg: (code) => `Zdravo Hotel La Rosa Negra! Želim direktnu rezervaciju sa mojim 10% popust kodom: ${code}`,
  },
  sq: {
    eyebrow: 'Oferta juaj personale', title: 'Ju keni 10% zbritje për rezervim direkt',
    body: 'Rezervoni direkt me Hotel La Rosa Negra dhe kurseni. Na dërgoni mesazh në WhatsApp me kodin tuaj dhe ne do të aplikojmë zbritjen menjëherë.',
    copy: 'Kopjo kodin', copied: 'U kopjua!', whatsapp: 'Përdore në WhatsApp',
    expires: 'Vlen deri', backHome: 'Vizito hotelin',
    expired: 'Ky kod ka skaduar', redeemed: 'Ky kod tashmë është përdorur', notFound: 'Kodi nuk u gjet',
    expiredBody: 'Na dërgoni mesazh në WhatsApp gjithsesi.',
    whatsappMsg: (code) => `Përshëndetje Hotel La Rosa Negra! Dua të rezervoj direkt me kodin tim 10%: ${code}`,
  },
  de: {
    eyebrow: 'Ihr persönliches Angebot', title: 'Sie erhalten 10% Rabatt direkt',
    body: 'Buchen Sie direkt bei Hotel La Rosa Negra und sparen. Senden Sie uns Ihren Code auf WhatsApp — wir wenden den Rabatt sofort an.',
    copy: 'Code kopieren', copied: 'Kopiert!', whatsapp: 'Auf WhatsApp einlösen',
    expires: 'Gültig bis', backHome: 'Zum Hotel',
    expired: 'Dieser Code ist abgelaufen', redeemed: 'Dieser Code wurde bereits verwendet', notFound: 'Code nicht gefunden',
    expiredBody: 'Schreiben Sie uns trotzdem auf WhatsApp.',
    whatsappMsg: (code) => `Hallo Hotel La Rosa Negra! Ich möchte direkt buchen mit meinem 10% Rabattcode: ${code}`,
  },
  ru: {
    eyebrow: 'Персональное предложение', title: 'Вам скидка 10% при прямом бронировании',
    body: 'Бронируйте напрямую в Hotel La Rosa Negra и экономьте. Напишите нам в WhatsApp с этим кодом — скидку применим сразу.',
    copy: 'Скопировать код', copied: 'Скопировано!', whatsapp: 'Использовать в WhatsApp',
    expires: 'Действует до', backHome: 'Перейти к отелю',
    expired: 'Этот код истёк', redeemed: 'Этот код уже использован', notFound: 'Код не найден',
    expiredBody: 'Всё равно напишите нам в WhatsApp.',
    whatsappMsg: (code) => `Здравствуйте, Hotel La Rosa Negra! Хочу забронировать напрямую по моему коду скидки 10%: ${code}`,
  },
  pl: {
    eyebrow: 'Twoja oferta osobista', title: 'Masz 10% zniżki na rezerwację bezpośrednią',
    body: 'Zarezerwuj bezpośrednio w Hotel La Rosa Negra i oszczędzaj. Napisz do nas na WhatsApp z tym kodem — od razu naliczymy zniżkę.',
    copy: 'Kopiuj kod', copied: 'Skopiowano!', whatsapp: 'Zrealizuj na WhatsApp',
    expires: 'Ważny do', backHome: 'Przejdź do hotelu',
    expired: 'Ten kod wygasł', redeemed: 'Ten kod został już wykorzystany', notFound: 'Kod nie znaleziony',
    expiredBody: 'Napisz do nas na WhatsApp mimo to.',
    whatsappMsg: (code) => `Cześć Hotel La Rosa Negra! Chciałbym zarezerwować bezpośrednio z moim kodem rabatowym 10%: ${code}`,
  },
  hu: {
    eyebrow: 'Az Ön személyes ajánlata', title: '10% kedvezmény közvetlen foglalással',
    body: 'Foglaljon közvetlenül a Hotel La Rosa Negra-nál és spóroljon. Írjon nekünk WhatsApp-on a kódjával — azonnal levonjuk a kedvezményt.',
    copy: 'Kód másolása', copied: 'Másolva!', whatsapp: 'Beváltás WhatsApp-on',
    expires: 'Érvényes', backHome: 'A hotelhez',
    expired: 'Ez a kód lejárt', redeemed: 'Ezt a kódot már felhasználták', notFound: 'A kód nem található',
    expiredBody: 'Írjon nekünk WhatsApp-on mégis.',
    whatsappMsg: (code) => `Üdv Hotel La Rosa Negra! Szeretnék közvetlenül foglalni a 10% kedvezménykódommal: ${code}`,
  },
};

const CouponRedeem = () => {
  const { code: codeParam } = useParams();
  const code = (codeParam || '').toUpperCase();
  const { hotelInfo } = useHotel();
  const { lang } = useLanguage();
  const t = T[lang] || T.en;

  const [state, setState] = useState({ loading: true, error: '', coupon: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/coupons/${encodeURIComponent(code)}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'notFound' : 'server');
        const j = await res.json();
        if (!cancel) {
          setState({ loading: false, error: '', coupon: j });
          // Remember this coupon so the site header widget can promote it
          // across the guest's next visits.
          if (j && j.status !== 'redeemed' && j.status !== 'expired') {
            try {
              localStorage.setItem('rn_active_coupon', JSON.stringify({
                code: j.code, discount_pct: j.discount_pct,
                expires_at: j.expires_at, loyalty_tier: j.loyalty_tier,
                perk: j.perk, saved_at: new Date().toISOString(),
              }));
            } catch { /* ignore quota / private-mode */ }
          }
        }
      } catch (e) {
        if (!cancel) setState({ loading: false, error: e.message, coupon: null });
      }
    })();
    return () => { cancel = true; };
  }, [code]);

  const waHref = useMemo(() => {
    if (!hotelInfo?.whatsapp || !state.coupon) return null;
    return `https://wa.me/${hotelInfo.whatsapp}?text=${encodeURIComponent(t.whatsappMsg(state.coupon.code))}`;
  }, [hotelInfo, state.coupon, t]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {}
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const errorTitle =
    state.error === 'notFound' ? t.notFound :
    state.coupon?.status === 'expired' ? t.expired :
    state.coupon?.status === 'redeemed' ? t.redeemed : null;

  const isBlocked = errorTitle && (state.error || ['expired', 'redeemed'].includes(state.coupon?.status));

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50 flex items-center justify-center py-10 px-4" data-testid="coupon-redeem-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header gradient */}
          <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 text-white p-6 relative overflow-hidden">
            <div className="absolute -top-14 -right-14 w-40 h-40 bg-white/15 rounded-full blur-2xl" />
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-100">
              <Gift className="w-4 h-4" /> {t.eyebrow}
            </div>
            <h1 className="mt-2 text-2xl font-bold leading-tight">{t.title}</h1>
          </div>

          {isBlocked ? (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">{errorTitle}</p>
                  <p className="text-sm text-red-600 mt-1">{t.expiredBody}</p>
                </div>
              </div>
              {waHref && state.coupon && (
                <a href={waHref} target="_blank" rel="noopener noreferrer"
                   className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold shadow"
                   data-testid="coupon-page-whatsapp">
                  <MessageCircle className="w-5 h-5" /> {t.whatsapp}
                </a>
              )}
              <Link to="/" className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 px-4 py-2 text-sm">
                <Home className="w-4 h-4" /> {t.backHome}
              </Link>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              <p className="text-gray-600 text-sm leading-relaxed">{t.body}</p>

              {state.coupon.perk && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 flex items-center gap-3" data-testid="redeem-perk">
                  <span className="text-2xl">🌟</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-yellow-700 font-bold">VIP unlocked</p>
                    <p className="text-sm font-semibold text-yellow-800">{state.coupon.perk}</p>
                  </div>
                </div>
              )}

              <div className="bg-orange-50 border-2 border-dashed border-orange-300 rounded-xl p-5 text-center">
                <p className="text-[11px] uppercase tracking-widest text-orange-600 font-semibold mb-2">Code</p>
                <p className="font-mono text-3xl font-bold text-orange-700 tracking-wider select-all" data-testid="coupon-page-code">
                  {state.coupon.code}
                </p>
                <p className="text-xs text-orange-500 mt-2">
                  {t.expires} {new Date(state.coupon.expires_at).toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                <button onClick={copy}
                  className="mt-3 inline-flex items-center gap-2 bg-white border border-orange-300 text-orange-600 hover:bg-orange-100 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide"
                  data-testid="coupon-page-copy">
                  {copied ? <><Check className="w-3.5 h-3.5 text-green-500" /> {t.copied}</> : <><Copy className="w-3.5 h-3.5" /> {t.copy}</>}
                </button>
              </div>

              {waHref && (
                <a href={waHref} target="_blank" rel="noopener noreferrer"
                   className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold shadow-md text-lg"
                   data-testid="coupon-page-whatsapp">
                  <MessageCircle className="w-5 h-5" /> {t.whatsapp}
                </a>
              )}

              <Link to="/" className="w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-800 px-4 py-2 text-sm">
                <Home className="w-4 h-4" /> {t.backHome}
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Hotel La Rosa Negra · Velika Plaža, Ulcinj, Montenegro
        </p>
      </div>
    </div>
  );
};

export default CouponRedeem;
